import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  Send,
  Settings2,
  ArrowUpRight,
  Sun,
  Moon,
  LogOut,
  PanelLeftClose,
  Menu,
  ChevronDown,
  Sparkles,
} from "./PixelIcons";
import { useTheme } from "@bitsandflames/web-components/theme";
import {
  BfAdminPixelIcon,
  type BfAdminIconName,
} from "@bitsandflames/web-components/layouts";
import { useState, createContext, useContext } from "react";
import { oidc, api, type RuntimeConfig, type User } from "../lib/api";
import { useToast } from "./ui";
export const WorkspaceContext = createContext<{
  config: RuntimeConfig;
  user: User;
}>({} as never);
export const useWorkspace = () => useContext(WorkspaceContext);
export default function Shell({
  config,
  user,
}: {
  config: RuntimeConfig;
  user: User;
}) {
  const { theme, toggleTheme } = useTheme();
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const links = [
    { to: "/questionnaires", icon: "resources", label: "Questionnaires" },
    { to: "/templates", icon: "applications", label: "Template library" },
    { to: "/assignments", icon: null, label: "Surveys" },
    { to: "/results", icon: "analytics", label: "Responses" },
  ];
  async function logout() {
    try {
      await api("/auth/logout", { method: "POST" });
      sessionStorage.removeItem("iron.staff-token");
      sessionStorage.setItem("iron.require-fresh-login", "true");
      if (config.issuer) await oidc(config).removeUser();
      location.assign("/");
    } catch (e) {
      toast((e as Error).message, true);
    }
  }
  return (
    <WorkspaceContext.Provider value={{ config, user }}>
      <div className="app-shell">
        <header className="app-navbar">
          <div className="brand">
            <button
              className="icon-button mobile-menu"
              aria-label="Open navigation"
              onClick={() => setMobile(!mobile)}
            >
              <Menu size={20} />
            </button>
            <Link to="/questionnaires" className="brand-link">
              <img src="/iron.svg" alt="" />
              <span>
                iron<span className="brand-dot">.</span>
              </span>
            </Link>
            <span className="brand-divider" />
            <span className="app-description">Questionnaires & surveys</span>
          </div>
          <div className="navbar-right">
            <span className="connection-pill">
              <span /> {config.demo ? "Local workspace" : config.clinicName}
            </span>
            {config.sparkUrl && (
              <a className="spark-link" href={config.sparkUrl}>
                Open Spark <ArrowUpRight size={14} />
              </a>
            )}
            <button
              className="icon-button"
              aria-label={
                theme === "light"
                  ? "Switch to dark theme"
                  : "Switch to light theme"
              }
              onClick={toggleTheme}
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button
              className="avatar"
              aria-label="Workspace settings"
              onClick={() => navigate("/settings")}
            >
              {config.demo ? "BF" : user.name.slice(0, 2).toUpperCase()}
            </button>
          </div>
        </header>
        <aside className={`sidebar ${mobile ? "sidebar-open" : ""}`}>
          <div className="workspace-label">
            <div className="workspace-monogram">
              B<span>&</span>F
            </div>
            <div>
              <strong>
                {config.demo ? "Clinical workspace" : config.clinicName}
              </strong>
              <small>
                {config.demo
                  ? "Synthetic data · development"
                  : "Questionnaire workspace"}
              </small>
            </div>
            <ChevronDown size={14} />
          </div>
          <div className="sidebar-section-label">WORKSPACE</div>
          <nav>
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setMobile(false)}>
                {l.icon ? (
                  <BfAdminPixelIcon
                    name={l.icon as BfAdminIconName}
                    size={18}
                  />
                ) : (
                  <Send size={18} />
                )}
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-divider" />
          <div className="sidebar-note">
            <div className="note-mark">
              <Sparkles size={28} />
            </div>
            <strong>
              Better questions.
              <br />
              Better understanding.
            </strong>
            <p>Give every patient a voice in their care.</p>
            <Link to="/templates">
              Explore templates <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="sidebar-bottom">
            <NavLink to="/settings">
              <Settings2 size={18} />
              Settings
            </NavLink>
            <button onClick={logout}>
              <LogOut size={18} />
              Sign out
            </button>
            <div className="built-by">
              Built by <strong>Bits & Flames</strong>
              <PanelLeftClose size={14} />
            </div>
          </div>
        </aside>
        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </WorkspaceContext.Provider>
  );
}
